import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction as firebaseRunTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { awaitRemoteConfirmation, requireConnectionForMutation } from './connectivityService';
import {
  RECURRENCE_MATERIALIZATION_ERROR_CODES,
  RECURRENCE_INVALID_DAY_POLICIES,
  RECURRING_OCCURRENCE_STATE,
  FIRESTORE_TRANSACTION_STATUS,
  RecurrenceMaterializationError,
  buildFirestoreTransaction,
  RECURRING_SEGMENT_STATUS,
  buildRecurringOccurrenceStatePayload,
  buildRecurringSegmentPayload,
  createRecurringTransactionId,
  createOccurrenceKey,
  fromFirestoreRecurringOccurrenceState,
  fromFirestoreRecurringSegment,
  generateRecurrenceOccurrences,
  fromFirestoreStatus,
  fromFirestoreTransaction,
  fromFirestoreType,
  isValidCivilDate,
  toDomainRecurrenceRule,
  prepareRecurringSeriesSplit,
  isDateInSplitSchedule,
  RecurrenceSplitError,
  RECURRENCE_SPLIT_ERROR_CODES,
} from '../domain/finance';

const runTransaction = (firestoreDb, updateFunction) => awaitRemoteConfirmation(
  firebaseRunTransaction(firestoreDb, updateFunction)
);

export const createRecurringSeriesId = () => {
  const randomId = window.crypto?.randomUUID?.().replace(/-/g, '')
    || `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `series_${randomId}`;
};

const SEGMENT_UPDATE_KEYS = ['template', 'schedule', 'effectiveFromDate', 'effectiveUntilDate'];

const assertId = (value, code) => {
  if (typeof value !== 'string' || !value) throw new Error(code);
};

const assertExactChangeKeys = (changes) => {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) {
    throw new Error('INVALID_RECURRING_SEGMENT_CHANGES');
  }
  const keys = Object.keys(changes);
  if (keys.length === 0 || keys.some((key) => !SEGMENT_UPDATE_KEYS.includes(key))) {
    throw new Error('INVALID_RECURRING_SEGMENT_CHANGES');
  }
};

const getSegmentsCollection = (firestoreDb, accountId) => (
  collection(firestoreDb, 'accounts', accountId, 'recurringTransactions')
);

export const getRecurringSegmentRef = (firestoreDb, accountId, segmentId) => (
  doc(firestoreDb, 'accounts', accountId, 'recurringTransactions', segmentId)
);

const getOccurrenceStatesCollection = (firestoreDb, accountId) => (
  collection(firestoreDb, 'accounts', accountId, 'recurringOccurrenceStates')
);

export const getRecurringOccurrenceStateRef = (firestoreDb, accountId, occurrenceKey) => (
  doc(firestoreDb, 'accounts', accountId, 'recurringOccurrenceStates', occurrenceKey)
);

export const createRecurringSegment = async ({
  accountId,
  actorUid,
  seriesId,
  template,
  schedule,
  effectiveFromDate,
  effectiveUntilDate = null,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  const payload = buildRecurringSegmentPayload({
    seriesId,
    status: RECURRING_SEGMENT_STATUS.ACTIVE,
    template,
    schedule,
    effectiveFromDate,
    effectiveUntilDate,
    revision: 1,
    createdByUid: actorUid,
    updatedByUid: actorUid,
  });
  const created = await awaitRemoteConfirmation(addDoc(getSegmentsCollection(firestoreDb, accountId), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }));
  return { id: created.id, ...payload };
};

export const createRecurringTransactionFromDraft = async ({
  accountId,
  actorUid,
  transaction,
  recurrence,
  seriesId = createRecurringSeriesId(),
}, firestoreDb = firestore) => {
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertId(seriesId, 'RECURRING_SERIES_REQUIRED');

  const transactionPayload = buildFirestoreTransaction(transaction);
  if (!transactionPayload) throw new Error('INVALID_RECURRING_TRANSACTION_TEMPLATE');
  if (!transactionPayload.name.trim()) throw new Error('RECURRING_NAME_REQUIRED');

  const segment = await createRecurringSegment({
    accountId,
    actorUid,
    seriesId,
    template: {
      type: transactionPayload.type,
      amountCents: transactionPayload.amountCents,
      category: transactionPayload.category,
      name: transactionPayload.name,
      ...(transactionPayload.attributedToUid ? { attributedToUid: transactionPayload.attributedToUid } : {}),
      ...(transactionPayload.attributedToNameSnapshot ? { attributedToNameSnapshot: transactionPayload.attributedToNameSnapshot } : {}),
    },
    schedule: {
      unit: recurrence?.unit,
      interval: recurrence?.interval,
      anchorDate: transactionPayload.date,
      untilDate: recurrence?.untilDate ?? null,
      invalidDayPolicy: RECURRENCE_INVALID_DAY_POLICIES.CLAMP,
    },
    effectiveFromDate: transactionPayload.date,
    effectiveUntilDate: null,
  }, firestoreDb);

  if (transactionPayload.status === FIRESTORE_TRANSACTION_STATUS.PENDING) {
    return {
      segment,
      materialization: null,
      firstOccurrenceVirtual: true,
    };
  }

  const occurrenceKey = createOccurrenceKey(seriesId, transactionPayload.date);
  const materialization = await materializeRecurringOccurrence({
    accountId,
    actorUid,
    segmentId: segment.id,
    scheduledDate: transactionPayload.date,
    occurrenceKey,
    status: transactionPayload.status,
  }, firestoreDb);

  return {
    segment,
    materialization,
    firstOccurrenceVirtual: false,
  };
};

export const updateRecurringSegment = async ({
  accountId,
  segmentId,
  actorUid,
  changes,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertExactChangeKeys(changes);
  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);

  return runTransaction(firestoreDb, async (transaction) => {
    const snapshot = await transaction.get(segmentRef);
    if (!snapshot.exists()) throw new Error('RECURRING_SEGMENT_NOT_FOUND');
    const current = fromFirestoreRecurringSegment(snapshot.id, snapshot.data());
    if (current.status === RECURRING_SEGMENT_STATUS.CANCELLED) {
      throw new Error('RECURRING_SEGMENT_CANCELLED');
    }
    const payload = buildRecurringSegmentPayload({
      seriesId: current.seriesId,
      status: current.status,
      template: changes.template ?? current.template,
      schedule: changes.schedule ?? current.schedule,
      effectiveFromDate: changes.effectiveFromDate ?? current.effectiveFromDate,
      effectiveUntilDate: Object.prototype.hasOwnProperty.call(changes, 'effectiveUntilDate')
        ? changes.effectiveUntilDate
        : current.effectiveUntilDate,
      revision: current.revision + 1,
      createdByUid: current.createdByUid,
      updatedByUid: actorUid,
    });
    transaction.update(segmentRef, { ...payload, updatedAt: serverTimestamp() });
    return { id: segmentId, ...payload, createdAt: current.createdAt };
  });
};

export const cancelRecurringSegment = async ({
  accountId,
  segmentId,
  actorUid,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);

  return runTransaction(firestoreDb, async (transaction) => {
    const snapshot = await transaction.get(segmentRef);
    if (!snapshot.exists()) throw new Error('RECURRING_SEGMENT_NOT_FOUND');
    const current = fromFirestoreRecurringSegment(snapshot.id, snapshot.data());
    if (current.status === RECURRING_SEGMENT_STATUS.CANCELLED) return current;
    const payload = buildRecurringSegmentPayload({
      seriesId: current.seriesId,
      status: RECURRING_SEGMENT_STATUS.CANCELLED,
      template: current.template,
      schedule: current.schedule,
      effectiveFromDate: current.effectiveFromDate,
      effectiveUntilDate: current.effectiveUntilDate,
      revision: current.revision + 1,
      createdByUid: current.createdByUid,
      updatedByUid: actorUid,
    });
    transaction.update(segmentRef, { ...payload, updatedAt: serverTimestamp() });
    return { id: segmentId, ...payload, createdAt: current.createdAt };
  });
};

export const getRelevantRecurringSegments = async ({
  accountId,
  startDate,
  endDate,
}, firestoreDb = firestore) => {
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  if (!isValidCivilDate(startDate) || !isValidCivilDate(endDate) || startDate > endDate) {
    throw new Error('INVALID_RECURRENCE_RANGE');
  }
  const snapshot = await getDocs(query(
    getSegmentsCollection(firestoreDb, accountId),
    where('status', '==', RECURRING_SEGMENT_STATUS.ACTIVE),
    where('effectiveFromDate', '<=', endDate),
    orderBy('effectiveFromDate', 'asc')
  ));
  return snapshot.docs
    .map((item) => fromFirestoreRecurringSegment(item.id, item.data()))
    .filter((segment) => (
      (!segment.effectiveUntilDate || segment.effectiveUntilDate >= startDate)
      && (!segment.schedule.untilDate || segment.schedule.untilDate >= startDate)
    ));
};

export const getRecurringSegmentsBySeries = async ({ accountId, seriesId }, firestoreDb = firestore) => {
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(seriesId, 'RECURRING_SERIES_REQUIRED');
  const snapshot = await getDocs(query(
    getSegmentsCollection(firestoreDb, accountId),
    where('seriesId', '==', seriesId)
  ));
  return snapshot.docs.map((item) => fromFirestoreRecurringSegment(item.id, item.data()));
};

export const createRecurringOccurrenceState = async ({
  accountId,
  actorUid,
  occurrenceKey,
  seriesId,
  segmentId,
  scheduledDate,
  effectiveDate,
  state,
  transactionId,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  const occurrenceRef = getRecurringOccurrenceStateRef(firestoreDb, accountId, occurrenceKey);
  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);

  return runTransaction(firestoreDb, async (transaction) => {
    const segmentSnapshot = await transaction.get(segmentRef);
    if (!segmentSnapshot.exists()) throw new Error('RECURRING_SEGMENT_NOT_FOUND');
    const segment = fromFirestoreRecurringSegment(segmentSnapshot.id, segmentSnapshot.data());
    if (segment.seriesId !== seriesId) throw new Error('RECURRING_SERIES_MISMATCH');
    const generated = generateRecurrenceOccurrences(
      toDomainRecurrenceRule(segment),
      scheduledDate,
      scheduledDate
    );
    if (generated.length !== 1 || generated[0].occurrenceKey !== occurrenceKey) {
      throw new Error('INVALID_RECURRING_OCCURRENCE');
    }

    if (state === RECURRING_OCCURRENCE_STATE.MATERIALIZED) {
      assertId(transactionId, 'RECURRING_TRANSACTION_REQUIRED');
      const transactionSnapshot = await transaction.get(
        doc(firestoreDb, 'accounts', accountId, 'transactions', transactionId)
      );
      if (!transactionSnapshot.exists()) throw new Error('RECURRING_TRANSACTION_NOT_FOUND');
    }
    const existingState = await transaction.get(occurrenceRef);
    if (existingState.exists()) throw new Error('RECURRING_OCCURRENCE_STATE_EXISTS');

    const payload = buildRecurringOccurrenceStatePayload({
      occurrenceKey,
      seriesId,
      segmentId,
      scheduledDate,
      effectiveDate,
      state,
      ...(transactionId ? { transactionId } : {}),
      createdByUid: actorUid,
      updatedByUid: actorUid,
    });
    transaction.set(occurrenceRef, {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return payload;
  });
};

const materializationError = (code, details = []) => (
  new RecurrenceMaterializationError(code, details)
);

export const materializeRecurringOccurrence = async ({
  accountId,
  actorUid,
  segmentId,
  scheduledDate,
  occurrenceKey,
  status,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  if (!isValidCivilDate(scheduledDate) || typeof occurrenceKey !== 'string') {
    throw materializationError(RECURRENCE_MATERIALIZATION_ERROR_CODES.INVALID_OCCURRENCE);
  }

  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);

  return runTransaction(firestoreDb, async (transaction) => {
    const segmentSnapshot = await transaction.get(segmentRef);
    if (!segmentSnapshot.exists()) throw new Error('RECURRING_SEGMENT_NOT_FOUND');
    const segment = fromFirestoreRecurringSegment(segmentSnapshot.id, segmentSnapshot.data());
    const generated = generateRecurrenceOccurrences(
      toDomainRecurrenceRule(segment),
      scheduledDate,
      scheduledDate
    );
    if (generated.length !== 1 || generated[0].occurrenceKey !== occurrenceKey) {
      throw materializationError(RECURRENCE_MATERIALIZATION_ERROR_CODES.INVALID_OCCURRENCE);
    }

    const transactionId = createRecurringTransactionId(segment.seriesId, scheduledDate);
    const occurrenceRef = getRecurringOccurrenceStateRef(
      firestoreDb,
      accountId,
      occurrenceKey
    );
    const transactionRef = doc(
      firestoreDb,
      'accounts',
      accountId,
      'transactions',
      transactionId
    );
    const occurrenceSnapshot = await transaction.get(occurrenceRef);
    const transactionSnapshot = await transaction.get(transactionRef);

    if (occurrenceSnapshot.exists()) {
      const rawState = occurrenceSnapshot.data();
      if (
        rawState.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED
        && rawState.transactionId !== transactionId
      ) {
        throw materializationError(
          RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_TRANSACTION_MISMATCH
        );
      }
      let existingState;
      try {
        existingState = fromFirestoreRecurringOccurrenceState(
          occurrenceSnapshot.id,
          rawState
        );
      } catch (error) {
        throw materializationError(
          RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_INCONSISTENT,
          [error.code || error.message]
        );
      }
      if (existingState.state === RECURRING_OCCURRENCE_STATE.SKIPPED) {
        throw materializationError(RECURRENCE_MATERIALIZATION_ERROR_CODES.OCCURRENCE_SKIPPED);
      }
      if (
        existingState.seriesId !== segment.seriesId
        || existingState.segmentId !== segmentId
        || existingState.scheduledDate !== scheduledDate
      ) {
        throw materializationError(RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_INCONSISTENT);
      }
      if (existingState.transactionId !== transactionId) {
        throw materializationError(
          RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_TRANSACTION_MISMATCH
        );
      }
      if (!transactionSnapshot.exists()) {
        throw materializationError(
          RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_TRANSACTION_MISSING
        );
      }
      return {
        transaction: { id: transactionId, ...transactionSnapshot.data() },
        occurrenceState: existingState,
        alreadyMaterialized: true,
      };
    }

    if (transactionSnapshot.exists()) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.TRANSACTION_WITHOUT_STATE
      );
    }
    if (segment.status === RECURRING_SEGMENT_STATUS.CANCELLED) {
      throw materializationError(RECURRENCE_MATERIALIZATION_ERROR_CODES.SEGMENT_CANCELLED);
    }

    const transactionPayload = buildFirestoreTransaction({
      nome: segment.template.name,
      categoria: segment.template.category,
      tipo: fromFirestoreType(segment.template.type),
      status: fromFirestoreStatus(status),
      amountCents: segment.template.amountCents,
      date: scheduledDate,
      createdByUid: actorUid,
      attributedToUid: segment.template.attributedToUid,
      attributedToNameSnapshot: segment.template.attributedToNameSnapshot,
    });
    if (!transactionPayload) {
      throw materializationError(RECURRENCE_MATERIALIZATION_ERROR_CODES.INVALID_STATUS);
    }
    const occurrencePayload = buildRecurringOccurrenceStatePayload({
      occurrenceKey,
      seriesId: segment.seriesId,
      segmentId,
      scheduledDate,
      effectiveDate: scheduledDate,
      state: RECURRING_OCCURRENCE_STATE.MATERIALIZED,
      transactionId,
      createdByUid: actorUid,
      updatedByUid: actorUid,
    });
    const createdAt = serverTimestamp();
    transaction.set(transactionRef, {
      ...transactionPayload,
      createdAt,
      updatedAt: createdAt,
    });
    transaction.set(occurrenceRef, {
      ...occurrencePayload,
      createdAt,
      updatedAt: createdAt,
    });
    return {
      transaction: { id: transactionId, ...transactionPayload },
      occurrenceState: { id: occurrenceKey, ...occurrencePayload },
      alreadyMaterialized: false,
    };
  });
};
export const updateMaterializedRecurringOccurrence = async ({
  accountId,
  actorUid,
  occurrenceKey,
  transaction: transactionInput,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertId(occurrenceKey, 'RECURRING_OCCURRENCE_REQUIRED');
  assertId(transactionInput?.id, 'RECURRING_TRANSACTION_REQUIRED');

  const transactionPayload = buildFirestoreTransaction(transactionInput);
  const clearsAttribution = transactionInput?.attributedToUid === null;
  if (!transactionPayload) {
    throw materializationError(
      RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_INVALID_TRANSACTION
    );
  }

  const occurrenceRef = getRecurringOccurrenceStateRef(
    firestoreDb,
    accountId,
    occurrenceKey
  );
  const transactionRef = doc(
    firestoreDb,
    'accounts',
    accountId,
    'transactions',
    transactionInput.id
  );

  return runTransaction(firestoreDb, async (transaction) => {
    const occurrenceSnapshot = await transaction.get(occurrenceRef);
    if (!occurrenceSnapshot.exists()) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_STATE_MISSING
      );
    }

    let occurrenceState;
    try {
      occurrenceState = fromFirestoreRecurringOccurrenceState(
        occurrenceSnapshot.id,
        occurrenceSnapshot.data()
      );
    } catch (error) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_INCONSISTENT,
        [error.code || error.message]
      );
    }

    if (occurrenceState.state !== RECURRING_OCCURRENCE_STATE.MATERIALIZED) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_NOT_MATERIALIZED
      );
    }
    if (occurrenceState.transactionId !== transactionInput.id) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_TRANSACTION_MISMATCH
      );
    }

    const persistedTransaction = await transaction.get(transactionRef);
    if (!persistedTransaction.exists()) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_TRANSACTION_MISSING
      );
    }

    const segmentRef = getRecurringSegmentRef(
      firestoreDb,
      accountId,
      occurrenceState.segmentId
    );
    const segmentSnapshot = await transaction.get(segmentRef);
    if (!segmentSnapshot.exists()) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_SEGMENT_MISSING
      );
    }

    const segment = fromFirestoreRecurringSegment(
      segmentSnapshot.id,
      segmentSnapshot.data()
    );
    const generated = generateRecurrenceOccurrences(
      toDomainRecurrenceRule(segment),
      occurrenceState.scheduledDate,
      occurrenceState.scheduledDate
    );
    if (
      segment.seriesId !== occurrenceState.seriesId
      || generated.length !== 1
      || generated[0].occurrenceKey !== occurrenceState.occurrenceKey
    ) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_SEGMENT_MISMATCH
      );
    }

    const persistedData = persistedTransaction.data();
    if (persistedData.date !== occurrenceState.effectiveDate) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.STATE_INCONSISTENT
      );
    }
    if (persistedData.type !== transactionPayload.type) {
      throw materializationError(
        RECURRENCE_MATERIALIZATION_ERROR_CODES.UPDATE_TYPE_MISMATCH
      );
    }

    const occurrencePayload = buildRecurringOccurrenceStatePayload({
      occurrenceKey: occurrenceState.occurrenceKey,
      seriesId: occurrenceState.seriesId,
      segmentId: occurrenceState.segmentId,
      scheduledDate: occurrenceState.scheduledDate,
      effectiveDate: transactionPayload.date,
      state: RECURRING_OCCURRENCE_STATE.MATERIALIZED,
      transactionId: occurrenceState.transactionId,
      createdByUid: occurrenceState.createdByUid,
      updatedByUid: actorUid,
    });
    const updatedAt = serverTimestamp();

    transaction.update(transactionRef, {
      ...transactionPayload,
      ...(clearsAttribution ? { attributedToUid: deleteField(), attributedToNameSnapshot: deleteField() } : {}),
      updatedAt,
    });
    transaction.update(occurrenceRef, {
      ...occurrencePayload,
      updatedAt,
    });

    return {
      transaction: fromFirestoreTransaction(transactionInput.id, {
        ...persistedData,
        ...transactionPayload,
        ...(clearsAttribution ? { attributedToUid: null, attributedToNameSnapshot: null } : {}),
        updatedAt,
      }),
      occurrenceState: {
        ...occurrenceState,
        ...occurrencePayload,
        updatedAt,
      },
    };
  });
};

export const getRecurringOccurrenceState = async ({ accountId, occurrenceKey }, firestoreDb = firestore) => {
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(occurrenceKey, 'RECURRING_OCCURRENCE_REQUIRED');
  const snapshot = await getDoc(getRecurringOccurrenceStateRef(firestoreDb, accountId, occurrenceKey));
  return snapshot.exists()
    ? fromFirestoreRecurringOccurrenceState(snapshot.id, snapshot.data())
    : null;
};

export const getRecurringOccurrenceStateByTransactionId = async ({
  accountId,
  transactionId,
}, firestoreDb = firestore) => {
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(transactionId, 'RECURRING_TRANSACTION_REQUIRED');
  const snapshot = await getDocs(query(
    getOccurrenceStatesCollection(firestoreDb, accountId),
    where('transactionId', '==', transactionId),
    limit(2)
  ));
  if (snapshot.size > 1) throw new Error('DUPLICATE_RECURRING_TRANSACTION_LINK');
  const item = snapshot.docs[0];
  return item ? fromFirestoreRecurringOccurrenceState(item.id, item.data()) : null;
};




export const getRecurringSegment = async ({ accountId, segmentId }, firestoreDb = firestore) => {
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  const snapshot = await getDoc(getRecurringSegmentRef(firestoreDb, accountId, segmentId));
  if (!snapshot.exists()) throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.INVALID_INPUT);
  return fromFirestoreRecurringSegment(snapshot.id, snapshot.data());
};

const MAX_SPLIT_SIDECARS = 200;

export const RECURRING_DELETION_ERROR_CODES = Object.freeze({
  INVALID_INPUT: 'INVALID_RECURRING_DELETION_INPUT',
  INVALID_OCCURRENCE: 'INVALID_RECURRING_DELETION_OCCURRENCE',
  INCONSISTENT_STATE: 'RECURRING_DELETION_INCONSISTENT_STATE',
  REALIZED_CONFIRMATION_REQUIRED: 'RECURRING_DELETION_REALIZED_CONFIRMATION_REQUIRED',
  TOO_MANY_FUTURE_STATES: 'RECURRING_DELETION_TOO_MANY_FUTURE_STATES',
  CONFLICT: 'RECURRING_DELETION_CONFLICT',
});

export class RecurringDeletionError extends Error {
  constructor(code, details = []) {
    super(code);
    this.name = 'RecurringDeletionError';
    this.code = code;
    this.details = details;
  }
}

const deletionFail = (code, details = []) => {
  throw new RecurringDeletionError(code, details);
};

const validateDeletionOccurrence = (segment, segmentId, scheduledDate, occurrenceKey) => {
  if (!segment || segment.id !== segmentId || !isValidCivilDate(scheduledDate)
    || createOccurrenceKey(segment.seriesId, scheduledDate) !== occurrenceKey) {
    deletionFail(RECURRING_DELETION_ERROR_CODES.INVALID_INPUT);
  }
  const generated = generateRecurrenceOccurrences(
    toDomainRecurrenceRule(segment), scheduledDate, scheduledDate
  );
  if (generated.length !== 1 || generated[0].occurrenceKey !== occurrenceKey) {
    deletionFail(RECURRING_DELETION_ERROR_CODES.INVALID_OCCURRENCE);
  }
};

const skippedOccurrencePayload = ({ state, segment, segmentId, scheduledDate, occurrenceKey, actorUid }) => (
  buildRecurringOccurrenceStatePayload({
    occurrenceKey,
    seriesId: segment.seriesId,
    segmentId,
    scheduledDate,
    effectiveDate: scheduledDate,
    state: RECURRING_OCCURRENCE_STATE.SKIPPED,
    createdByUid: state?.createdByUid || actorUid,
    updatedByUid: actorUid,
  })
);

export const skipRecurringOccurrence = async ({
  accountId,
  actorUid,
  segmentId,
  scheduledDate,
  occurrenceKey,
  allowRealized = false,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  assertId(occurrenceKey, 'RECURRING_OCCURRENCE_REQUIRED');
  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);
  const stateRef = getRecurringOccurrenceStateRef(firestoreDb, accountId, occurrenceKey);

  return runTransaction(firestoreDb, async (firestoreTransaction) => {
    const [segmentSnapshot, stateSnapshot] = await Promise.all([
      firestoreTransaction.get(segmentRef),
      firestoreTransaction.get(stateRef),
    ]);
    if (!segmentSnapshot.exists()) deletionFail(RECURRING_DELETION_ERROR_CODES.INVALID_INPUT);
    const segment = fromFirestoreRecurringSegment(segmentSnapshot.id, segmentSnapshot.data());
    validateDeletionOccurrence(segment, segmentId, scheduledDate, occurrenceKey);
    const state = stateSnapshot.exists()
      ? fromFirestoreRecurringOccurrenceState(stateSnapshot.id, stateSnapshot.data())
      : null;
    if (state && (state.seriesId !== segment.seriesId || state.segmentId !== segmentId
      || state.scheduledDate !== scheduledDate)) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.INCONSISTENT_STATE);
    }
    if (state?.state === RECURRING_OCCURRENCE_STATE.SKIPPED) {
      return { occurrenceState: state, alreadySkipped: true, deletedTransactionId: null };
    }

    const transactionRef = state?.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED
      ? doc(firestoreDb, 'accounts', accountId, 'transactions', state.transactionId)
      : null;
    const transactionSnapshot = transactionRef
      ? await firestoreTransaction.get(transactionRef)
      : null;
    if (transactionRef && !transactionSnapshot?.exists()) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.INCONSISTENT_STATE, [state.transactionId]);
    }
    const status = transactionSnapshot?.data()?.status;
    const realized = status === FIRESTORE_TRANSACTION_STATUS.PAID
      || status === FIRESTORE_TRANSACTION_STATUS.RECEIVED;
    if (realized && !allowRealized) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.REALIZED_CONFIRMATION_REQUIRED);
    }
    if (transactionSnapshot && ![
      FIRESTORE_TRANSACTION_STATUS.PENDING,
      FIRESTORE_TRANSACTION_STATUS.PAID,
      FIRESTORE_TRANSACTION_STATUS.RECEIVED,
    ].includes(status)) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.INCONSISTENT_STATE, [state.transactionId]);
    }

    const payload = skippedOccurrencePayload({
      state, segment, segmentId, scheduledDate, occurrenceKey, actorUid,
    });
    const updatedAt = serverTimestamp();
    if (transactionRef) firestoreTransaction.delete(transactionRef);
    if (state) {
      firestoreTransaction.set(stateRef, {
        ...payload,
        createdAt: state.createdAt,
        updatedAt,
      });
    } else {
      firestoreTransaction.set(stateRef, { ...payload, createdAt: updatedAt, updatedAt });
    }
    return {
      occurrenceState: { ...payload, createdAt: state?.createdAt || updatedAt, updatedAt },
      alreadySkipped: false,
      deletedTransactionId: state?.transactionId || null,
    };
  });
};

export const cancelRecurringSeriesFromOccurrence = async ({
  accountId,
  actorUid,
  segmentId,
  expectedRevision,
  scheduledDate,
  occurrenceKey,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  assertId(occurrenceKey, 'RECURRING_OCCURRENCE_REQUIRED');
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 1) {
    deletionFail(RECURRING_DELETION_ERROR_CODES.INVALID_INPUT);
  }
  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);
  const initialSnapshot = await getDoc(segmentRef);
  if (!initialSnapshot.exists()) deletionFail(RECURRING_DELETION_ERROR_CODES.INVALID_INPUT);
  const initialSegment = fromFirestoreRecurringSegment(initialSnapshot.id, initialSnapshot.data());
  validateDeletionOccurrence(initialSegment, segmentId, scheduledDate, occurrenceKey);
  if (initialSegment.revision !== expectedRevision) {
    deletionFail(RECURRING_DELETION_ERROR_CODES.CONFLICT);
  }
  const statesSnapshot = await getDocs(query(
    getOccurrenceStatesCollection(firestoreDb, accountId),
    where('seriesId', '==', initialSegment.seriesId),
    where('scheduledDate', '>=', scheduledDate),
    orderBy('scheduledDate', 'asc'),
    limit(MAX_SPLIT_SIDECARS + 1)
  ));
  if (statesSnapshot.size > MAX_SPLIT_SIDECARS) {
    deletionFail(RECURRING_DELETION_ERROR_CODES.TOO_MANY_FUTURE_STATES);
  }
  const stateRefs = statesSnapshot.docs.map((snapshot) => snapshot.ref);
  const historySegmentRef = doc(getSegmentsCollection(firestoreDb, accountId));

  return runTransaction(firestoreDb, async (firestoreTransaction) => {
    const currentSnapshot = await firestoreTransaction.get(segmentRef);
    const stateSnapshots = await Promise.all(stateRefs.map((ref) => firestoreTransaction.get(ref)));
    if (!currentSnapshot.exists()) deletionFail(RECURRING_DELETION_ERROR_CODES.CONFLICT);
    const current = fromFirestoreRecurringSegment(currentSnapshot.id, currentSnapshot.data());
    if (current.revision !== expectedRevision || current.seriesId !== initialSegment.seriesId
      || current.status !== RECURRING_SEGMENT_STATUS.ACTIVE) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.CONFLICT);
    }
    validateDeletionOccurrence(current, segmentId, scheduledDate, occurrenceKey);
    const futureStates = stateSnapshots
      .filter((snapshot) => snapshot.exists())
      .map((snapshot) => fromFirestoreRecurringOccurrenceState(snapshot.id, snapshot.data()));
    if (futureStates.some((state) => state.seriesId !== current.seriesId
      || state.segmentId !== segmentId || state.scheduledDate < scheduledDate)) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.INCONSISTENT_STATE);
    }
    const transactionRefs = futureStates
      .filter((state) => state.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED)
      .map((state) => doc(firestoreDb, 'accounts', accountId, 'transactions', state.transactionId));
    const transactionSnapshots = await Promise.all(
      transactionRefs.map((ref) => firestoreTransaction.get(ref))
    );
    if (transactionSnapshots.some((snapshot) => !snapshot.exists())) {
      deletionFail(RECURRING_DELETION_ERROR_CODES.INCONSISTENT_STATE);
    }
    const transactionsById = new Map(transactionSnapshots.map((snapshot) => [
      snapshot.id, snapshot,
    ]));
    const pendingStates = [];
    let preservedRealized = 0;
    for (const state of futureStates) {
      if (state.state !== RECURRING_OCCURRENCE_STATE.MATERIALIZED) continue;
      const snapshot = transactionsById.get(state.transactionId);
      const status = snapshot?.data()?.status;
      if (status === FIRESTORE_TRANSACTION_STATUS.PENDING) pendingStates.push(state);
      else if ([
        FIRESTORE_TRANSACTION_STATUS.PAID,
        FIRESTORE_TRANSACTION_STATUS.RECEIVED,
      ].includes(status)) preservedRealized += 1;
      else deletionFail(RECURRING_DELETION_ERROR_CODES.INCONSISTENT_STATE, [state.transactionId]);
    }

    const historyOccurrences = generateRecurrenceOccurrences(
      toDomainRecurrenceRule(current), current.effectiveFromDate, scheduledDate
    );
    const previousOccurrence = historyOccurrences.at(-2)?.scheduledDate || null;
    const updatedAt = serverTimestamp();
    const cancelledPayload = buildRecurringSegmentPayload({
      seriesId: current.seriesId,
      status: RECURRING_SEGMENT_STATUS.CANCELLED,
      template: current.template,
      schedule: current.schedule,
      effectiveFromDate: current.effectiveFromDate,
      effectiveUntilDate: current.effectiveUntilDate,
      revision: current.revision + 1,
      createdByUid: current.createdByUid,
      updatedByUid: actorUid,
    });
    firestoreTransaction.update(segmentRef, { ...cancelledPayload, updatedAt });

    let historySegment = null;
    if (previousOccurrence) {
      const historyPayload = buildRecurringSegmentPayload({
        seriesId: current.seriesId,
        status: RECURRING_SEGMENT_STATUS.ACTIVE,
        template: current.template,
        schedule: current.schedule,
        effectiveFromDate: current.effectiveFromDate,
        effectiveUntilDate: previousOccurrence,
        revision: 1,
        createdByUid: actorUid,
        updatedByUid: actorUid,
      });
      firestoreTransaction.set(historySegmentRef, {
        ...historyPayload, createdAt: updatedAt, updatedAt,
      });
      historySegment = { id: historySegmentRef.id, ...historyPayload };
    }

    for (const state of pendingStates) {
      const transactionRef = doc(
        firestoreDb, 'accounts', accountId, 'transactions', state.transactionId
      );
      const stateRef = getRecurringOccurrenceStateRef(
        firestoreDb, accountId, state.occurrenceKey
      );
      const payload = skippedOccurrencePayload({
        state,
        segment: current,
        segmentId,
        scheduledDate: state.scheduledDate,
        occurrenceKey: state.occurrenceKey,
        actorUid,
      });
      firestoreTransaction.delete(transactionRef);
      firestoreTransaction.set(stateRef, {
        ...payload, createdAt: state.createdAt, updatedAt,
      });
    }

    return {
      cancelledSegment: { id: segmentId, ...cancelledPayload },
      historySegment,
      removedPendingTransactions: pendingStates.length,
      preservedRealizedTransactions: preservedRealized,
    };
  });
};
export const splitRecurringSeriesFromOccurrence = async ({
  accountId,
  actorUid,
  segmentId,
  expectedRevision,
  scheduledDate,
  anchorDate,
  transaction: transactionInput,
  recurrence,
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  assertId(accountId, 'RECURRING_ACCOUNT_REQUIRED');
  assertId(actorUid, 'RECURRING_ACTOR_REQUIRED');
  assertId(segmentId, 'RECURRING_SEGMENT_REQUIRED');
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 1) {
    throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.INVALID_INPUT);
  }
  const transactionPayload = buildFirestoreTransaction(transactionInput);
  const clearsAttribution = transactionInput?.attributedToUid === null;
  if (!transactionPayload || !transactionPayload.name.trim()) {
    throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.INVALID_INPUT);
  }

  const segmentRef = getRecurringSegmentRef(firestoreDb, accountId, segmentId);
  const initialSnapshot = await getDoc(segmentRef);
  if (!initialSnapshot.exists()) throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.INVALID_INPUT);
  const initialSegment = fromFirestoreRecurringSegment(initialSnapshot.id, initialSnapshot.data());
  if (initialSegment.revision !== expectedRevision) {
    throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.CONFLICT);
  }

  const sidecarsSnapshot = await getDocs(query(
    getOccurrenceStatesCollection(firestoreDb, accountId),
    where('seriesId', '==', initialSegment.seriesId),
    where('scheduledDate', '>=', scheduledDate),
    orderBy('scheduledDate', 'asc'),
    limit(MAX_SPLIT_SIDECARS + 1)
  ));
  if (sidecarsSnapshot.size > MAX_SPLIT_SIDECARS) {
    throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.FUTURE_STATE_AMBIGUOUS);
  }
  const sidecarRefs = sidecarsSnapshot.docs.map((snapshot) => snapshot.ref);
  const nextSegmentRef = doc(getSegmentsCollection(firestoreDb, accountId));

  return runTransaction(firestoreDb, async (firestoreTransaction) => {
    const currentSnapshot = await firestoreTransaction.get(segmentRef);
    const nextSegmentSnapshot = await firestoreTransaction.get(nextSegmentRef);
    const sidecarSnapshots = await Promise.all(sidecarRefs.map((ref) => firestoreTransaction.get(ref)));
    if (!currentSnapshot.exists() || nextSegmentSnapshot.exists()) {
      throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.CONFLICT);
    }
    const current = fromFirestoreRecurringSegment(currentSnapshot.id, currentSnapshot.data());
    if (current.revision !== expectedRevision || current.seriesId !== initialSegment.seriesId) {
      throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.CONFLICT);
    }

    const prepared = prepareRecurringSeriesSplit({
      segment: current,
      scheduledDate,
      anchorDate,
      template: {
        type: transactionPayload.type,
        amountCents: transactionPayload.amountCents,
        category: transactionPayload.category,
        name: transactionPayload.name,
        ...(!clearsAttribution && (transactionPayload.attributedToUid || current.template.attributedToUid)
          ? { attributedToUid: transactionPayload.attributedToUid || current.template.attributedToUid } : {}),
        ...(!clearsAttribution && (transactionPayload.attributedToNameSnapshot || current.template.attributedToNameSnapshot)
          ? { attributedToNameSnapshot: transactionPayload.attributedToNameSnapshot || current.template.attributedToNameSnapshot } : {}),
      },
      unit: recurrence?.unit,
      interval: recurrence?.interval,
      untilDate: recurrence?.untilDate ?? null,
    });
    const futureStates = sidecarSnapshots
      .filter((snapshot) => snapshot.exists())
      .map((snapshot) => fromFirestoreRecurringOccurrenceState(snapshot.id, snapshot.data()));
    if (futureStates.some((state) => state.seriesId !== current.seriesId || state.segmentId !== segmentId)) {
      throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.FUTURE_STATE_AMBIGUOUS);
    }

    const selectedOccurrenceKey = createOccurrenceKey(current.seriesId, scheduledDate);
    const selectedState = futureStates.find((state) => state.occurrenceKey === selectedOccurrenceKey) || null;
    if (selectedState?.state === RECURRING_OCCURRENCE_STATE.SKIPPED) {
      throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.FUTURE_STATE_AMBIGUOUS);
    }
    for (const state of futureStates) {
      if (state === selectedState && state.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED) continue;
      if (!isDateInSplitSchedule(prepared.nextSegment, state.scheduledDate)) {
        throw new RecurrenceSplitError(
          RECURRENCE_SPLIT_ERROR_CODES.FUTURE_STATE_AMBIGUOUS,
          [state.occurrenceKey]
        );
      }
    }

    const selectedTransactionRef = selectedState?.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED
      ? doc(firestoreDb, 'accounts', accountId, 'transactions', selectedState.transactionId)
      : null;
    const newOccurrenceKey = createOccurrenceKey(current.seriesId, anchorDate);
    const newOccurrenceRef = getRecurringOccurrenceStateRef(firestoreDb, accountId, newOccurrenceKey);
    const newTransactionId = createRecurringTransactionId(current.seriesId, anchorDate);
    const newTransactionRef = doc(firestoreDb, 'accounts', accountId, 'transactions', newTransactionId);
    const [selectedTransactionSnapshot, newOccurrenceSnapshot, newTransactionSnapshot] = await Promise.all([
      selectedTransactionRef ? firestoreTransaction.get(selectedTransactionRef) : Promise.resolve(null),
      newOccurrenceKey !== selectedOccurrenceKey ? firestoreTransaction.get(newOccurrenceRef) : Promise.resolve(null),
      !selectedState && transactionPayload.status !== FIRESTORE_TRANSACTION_STATUS.PENDING
        ? firestoreTransaction.get(newTransactionRef) : Promise.resolve(null),
    ]);
    if (selectedTransactionRef && (!selectedTransactionSnapshot?.exists()
      || selectedState.transactionId !== transactionInput?.id)) {
      throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.FUTURE_STATE_AMBIGUOUS);
    }
    if (newOccurrenceSnapshot?.exists() || newTransactionSnapshot?.exists()) {
      throw new RecurrenceSplitError(RECURRENCE_SPLIT_ERROR_CODES.CONFLICT);
    }

    const updatedAt = serverTimestamp();
    const oldPayload = buildRecurringSegmentPayload({
      seriesId: current.seriesId,
      status: prepared.previousSegment.status,
      template: current.template,
      schedule: current.schedule,
      effectiveFromDate: current.effectiveFromDate,
      effectiveUntilDate: prepared.previousSegment.effectiveUntilDate,
      revision: prepared.previousSegment.revision,
      createdByUid: current.createdByUid,
      updatedByUid: actorUid,
    });
    const nextPayload = buildRecurringSegmentPayload({
      ...prepared.nextSegment,
      createdByUid: actorUid,
      updatedByUid: actorUid,
    });
    firestoreTransaction.update(segmentRef, { ...oldPayload, updatedAt });
    firestoreTransaction.set(nextSegmentRef, { ...nextPayload, createdAt: updatedAt, updatedAt });

    for (const state of futureStates) {
      const stateRef = getRecurringOccurrenceStateRef(firestoreDb, accountId, state.occurrenceKey);
      const effectiveDate = state === selectedState ? anchorDate : state.effectiveDate;
      firestoreTransaction.update(stateRef, {
        segmentId: nextSegmentRef.id,
        effectiveDate,
        updatedByUid: actorUid,
        updatedAt,
      });
    }

    let materialization = null;
    if (selectedState) {
      firestoreTransaction.update(selectedTransactionRef, {
        ...transactionPayload,
        ...(clearsAttribution ? { attributedToUid: deleteField(), attributedToNameSnapshot: deleteField() } : {}),
        updatedAt,
      });
      if (newOccurrenceKey !== selectedOccurrenceKey) {
        const skippedPayload = buildRecurringOccurrenceStatePayload({
          occurrenceKey: newOccurrenceKey,
          seriesId: current.seriesId,
          segmentId: nextSegmentRef.id,
          scheduledDate: anchorDate,
          effectiveDate: anchorDate,
          state: RECURRING_OCCURRENCE_STATE.SKIPPED,
          createdByUid: actorUid,
          updatedByUid: actorUid,
        });
        firestoreTransaction.set(newOccurrenceRef, { ...skippedPayload, createdAt: updatedAt, updatedAt });
      }
      materialization = { transactionId: selectedState.transactionId, alreadyMaterialized: true };
    } else if (transactionPayload.status !== FIRESTORE_TRANSACTION_STATUS.PENDING) {
      const occurrencePayload = buildRecurringOccurrenceStatePayload({
        occurrenceKey: newOccurrenceKey,
        seriesId: current.seriesId,
        segmentId: nextSegmentRef.id,
        scheduledDate: anchorDate,
        effectiveDate: anchorDate,
        state: RECURRING_OCCURRENCE_STATE.MATERIALIZED,
        transactionId: newTransactionId,
        createdByUid: actorUid,
        updatedByUid: actorUid,
      });
      firestoreTransaction.set(newTransactionRef, { ...transactionPayload, createdByUid: actorUid, createdAt: updatedAt, updatedAt });
      firestoreTransaction.set(newOccurrenceRef, { ...occurrencePayload, createdAt: updatedAt, updatedAt });
      materialization = { transactionId: newTransactionId, alreadyMaterialized: false };
    }

    return {
      previousSegment: { id: segmentId, ...oldPayload },
      nextSegment: { id: nextSegmentRef.id, ...nextPayload },
      migratedOccurrenceStates: futureStates.length,
      materialization,
    };
  });
};
